import React, { useState } from "react";
import { ArrowUp, ArrowDown, Eye, EyeOff, SlidersHorizontal, RotateCcw, X } from "lucide-react";
import { Slider } from "../ui/slider";
import { useLayout, SECTION_LABELS } from "../../context/LayoutContext";

const FIELDS = [
  { key: "heading", label: "Başlık boyutu" },
  { key: "text", label: "Yazı boyutu" },
  { key: "icon", label: "İkon boyutu" },
  { key: "padding", label: "Bölüm boşluğu" },
];

const ScalePanel = ({ section, onClose }) => {
  const { device, setDevice, setScale, resetSection } = useLayout();
  const scale = section[device] || {};
  return (
    <div className="mt-2 w-[290px] rounded-2xl bg-charcoal text-white p-4 shadow-2xl border border-white/15" data-testid={`section-panel-${section.key}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-wider text-gold">{SECTION_LABELS[section.key]}</span>
        <button onClick={onClose} data-testid={`section-panel-close-${section.key}`} className="text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex gap-1 p-1 rounded-full bg-white/10 mb-4">
        {["desktop", "mobile"].map((d) => (
          <button
            key={d}
            onClick={() => setDevice(d)}
            data-testid={`device-${d}`}
            className={`flex-1 text-xs py-1.5 rounded-full transition-colors ${device === d ? "bg-gold text-charcoal font-semibold" : "text-white/70"}`}
          >
            {d === "desktop" ? "Masaüstü" : "Mobil"}
          </button>
        ))}
      </div>
      <div className="space-y-4">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="text-white/70">{f.label}</span>
              <span className="text-gold font-semibold">{Math.round((scale[f.key] ?? 1) * 100)}%</span>
            </div>
            <Slider
              value={[scale[f.key] ?? 1]}
              min={0.6}
              max={1.8}
              step={0.05}
              onValueChange={([v]) => setScale(section.key, f.key, v)}
              data-testid={`slider-${section.key}-${f.key}`}
            />
          </div>
        ))}
      </div>
      <button
        onClick={() => resetSection(section.key)}
        data-testid={`section-reset-${section.key}`}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 text-xs py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
      >
        <RotateCcw className="w-3.5 h-3.5" /> Sıfırla
      </button>
    </div>
  );
};

export const SectionShell = ({ section, children }) => {
  const { editing, move, toggle } = useLayout();
  const [open, setOpen] = useState(false);
  if (!section.visible && !editing) return null;

  const style = {
    "--hs-d": section.desktop.heading,
    "--ts-d": section.desktop.text,
    "--is-d": section.desktop.icon,
    "--ps-d": section.desktop.padding,
    "--hs-m": section.mobile.heading,
    "--ts-m": section.mobile.text,
    "--is-m": section.mobile.icon,
    "--ps-m": section.mobile.padding,
  };

  return (
    <div
      data-sec=""
      {...(section.key === "banner" ? {} : { "data-sec-pad": "" })}
      data-testid={`section-${section.key}`}
      style={style}
      className={`relative ${editing ? "outline outline-1 outline-dashed outline-gold/60" : ""} ${!section.visible ? "opacity-40" : ""}`}
    >
      {editing && (
        <div className="absolute top-3 end-3 z-[45] flex flex-col items-end" data-editor-chrome="">
          <div className="flex items-center gap-1 rounded-full bg-charcoal/95 border border-white/15 px-1.5 py-1 shadow-xl">
            <span className="text-[11px] text-gold px-2 hidden sm:inline">{SECTION_LABELS[section.key]}</span>
            <IconBtn onClick={() => move(section.key, -1)} testid={`section-up-${section.key}`} title="Yukarı"><ArrowUp className="w-4 h-4" /></IconBtn>
            <IconBtn onClick={() => move(section.key, 1)} testid={`section-down-${section.key}`} title="Aşağı"><ArrowDown className="w-4 h-4" /></IconBtn>
            <IconBtn onClick={() => toggle(section.key)} testid={`section-visibility-${section.key}`} title="Göster/Gizle">
              {section.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </IconBtn>
            <IconBtn onClick={() => setOpen((v) => !v)} testid={`section-settings-${section.key}`} title="Boyutlar"><SlidersHorizontal className="w-4 h-4" /></IconBtn>
          </div>
          {open && <ScalePanel section={section} onClose={() => setOpen(false)} />}
        </div>
      )}
      {children}
    </div>
  );
};

const IconBtn = ({ children, onClick, testid, title }) => (
  <button
    onClick={onClick}
    title={title}
    data-testid={testid}
    className="w-8 h-8 rounded-full flex items-center justify-center text-white/85 hover:bg-white/15 hover:text-white transition-colors"
  >
    {children}
  </button>
);
